// services/userService.js
import api from './api';

// Объект сервиса для работы с пользователями
const userService = {
  /**
   * Получение списка пользователей с пагинацией
   * @param {number} page - Номер страницы
   * @param {number} pageSize - Количество элементов на странице
   * @returns {Promise<Object>} - Список пользователей с метаданными пагинации
   */
  getUsers: async (page = 1, pageSize = 10) => {
    try {
      const response = await api.get(`/users?page=${page}&pageSize=${pageSize}`);
      
      // Проверка структуры ответа и адаптация для интерфейса
      return {
        data: response.data || [],
        totalPages: Math.ceil(response.headers['x-total-count'] / pageSize) || 1,
        currentPage: page,
        totalCount: response.headers['x-total-count'] || 0
      };
    } catch (error) {
      console.error('Ошибка при загрузке пользователей:', error);
      // Возвращаем пустой результат в случае ошибки
      return {
        data: [],
        totalPages: 1,
        currentPage: page,
        totalCount: 0
      };
    }
  },

  /**
   * Получение профиля текущего пользователя
   * @returns {Promise<Object>} - Данные профиля пользователя
   */
  getUserProfile: async () => {
    try {
      const response = await api.get('/users/profile');
      return response.data;
    } catch (error) {
      console.error('Ошибка при получении профиля пользователя:', error);
      throw error;
    }
  },

  /**
   * Получение пользователя по ID
   * @param {string} id - ID пользователя
   * @returns {Promise<Object>} - Данные пользователя
   */
  getUserById: async (id) => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Ошибка при получении пользователя ${id}:`, error);
      throw error;
    }
  },

  /**
   * Создание нового пользователя (только для администраторов)
   * @param {Object} userData - Данные нового пользователя
   * @returns {Promise<Object>} - Данные созданного пользователя
   */
  createUser: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Ошибка при создании пользователя:', error);
      throw error;
    }
  },

  /**
   * Обновление данных пользователя
   * @param {string} id - ID пользователя
   * @param {Object} userData - Новые данные пользователя
   * @returns {Promise<Object>} - Обновленные данные пользователя
   */
  updateUser: async (id, userData) => {
    try {
      const response = await api.put(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      console.error(`Ошибка при обновлении пользователя ${id}:`, error);
      throw error;
    }
  },

  /**
   * Обновление статуса пользователя
   * @param {string} id - ID пользователя
   * @param {string} status - Новый статус (Active, Inactive, Blocked)
   * @returns {Promise<Object>} - Результат операции
   */
  updateUserStatus: async (id, status) => {
    try {
      const response = await api.patch(`/users/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Ошибка при обновлении статуса пользователя ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Получение списка ролей пользователей
   * @returns {Promise<Array>} - Список доступных ролей
   */
  getRoles: async () => {
    try {
      const response = await api.get('/roles');
      return response.data || [];
    } catch (error) {
      console.error('Ошибка при получении списка ролей:', error);
      
      // Возвращаем запасной статичный список в случае ошибки
      return [
        { id: 1, name: 'admin', description: 'Администратор' },
        { id: 2, name: 'manager', description: 'Менеджер' },
        { id: 3, name: 'operator', description: 'Оператор' },
        { id: 4, name: 'analyst', description: 'Аналитик' }
      ];
    }
  }
};

export default userService;